/* Reducers follow the "ducks" method of exporting
  the constants, reducer and actions for the domain
  with the reducer being the default export.
*/
import Immutable from 'immutable';
import { reducerKeys } from 'edrolo/schemas';
import { createNormalizedStoreUpdater } from 'edrolo/reducers/utils';
import { trackAnalytics } from 'edrolo/analytics';
import { ORIGIN } from 'edrolo/constants/Analytics';

/* Constants */
export const actionTypes = {
  LOAD_CLASS_DETAIL_SIGNAL: 'edrolo/class/LOAD_CLASS_DETAIL_SIGNAL',
  LOAD_CLASS_DETAIL_FETCH: 'edrolo/class/LOAD_CLASS_DETAIL_FETCH',
  LOAD_CLASS_DETAIL_SUCCESS: 'edrolo/class/LOAD_CLASS_DETAIL_SUCCESS',
  LOAD_CLASS_DETAIL_FAILURE: 'edrolo/class/LOAD_CLASS_DETAIL_FAILURE',

  LOAD_PRIVILEGED_CONTENT_SIGNAL:
    'edrolo/class/LOAD_CLASS_PRIVILEGED_CONTENT_SIGNAL',
  LOAD_PRIVILEGED_CONTENT_FETCH: 'edrolo/class/LOAD_PRIVILEGED_CONTENT_FETCH',
  LOAD_PRIVILEGED_CONTENT_SUCCESS:
    'edrolo/class/LOAD_PRIVILEGED_CONTENT_SUCCESS',
  LOAD_PRIVILEGED_CONTENT_FAILURE:
    'edrolo/class/LOAD_PRIVILEGED_CONTENT_FAILURE',

  SAVE_TEACHER_NOTE_SIGNAL: 'edrolo/class/SAVE_TEACHER_NOTE_SIGNAL',
  SAVE_TEACHER_NOTE_FETCH: 'edrolo/class/SAVE_TEACHER_NOTE_FETCH',
  SAVE_TEACHER_NOTE_SUCCESS: 'edrolo/class/SAVE_TEACHER_NOTE_SUCCESS',
  SAVE_TEACHER_NOTE_FAILURE: 'edrolo/class/SAVE_TEACHER_NOTE_FAILURE',

  DELETE_TEACHER_NOTE_SIGNAL: 'edrolo/class/DELETE_TEACHER_NOTE_SIGNAL',
  DELETE_TEACHER_NOTE_FETCH: 'edrolo/class/DELETE_TEACHER_NOTE_FETCH',
  DELETE_TEACHER_NOTE_SUCCESS: 'edrolo/class/DELETE_TEACHER_NOTE_SUCCESS',
  DELETE_TEACHER_NOTE_FAILURE: 'edrolo/class/DELETE_TEACHER_NOTE_FAILURE',

  LOCK_LESSON_RESOURCE_SIGNAL: 'edrolo/class/LOCK_LESSON_RESOURCE_SIGNAL',
  LOCK_LESSON_RESOURCE_REQUEST: 'edrolo/class/LOCK_LESSON_RESOURCE_REQUEST',
  LOCK_LESSON_RESOURCE_SUCCESS: 'edrolo/class/LOCK_LESSON_RESOURCE_SUCCESS',
  LOCK_LESSON_RESOURCE_FAILURE: 'edrolo/class/LOCK_LESSON_RESOURCE_FAILURE',

  ANALYTICS_ACCESS_LESSON_RESOURCE:
    'edrolo/class/ANALYTICS_ACCESS_LESSON_RESOURCE',

  ANONYMIZE_PROFILES: 'edrolo/class/ANONYMIZE_PROFILES',
  DEANONYMIZE_PROFILES: 'edrolo/class/DEANONYMIZE_PROFILES',
};
/* Reducer */
const defaultState = Immutable.fromJS({
  [reducerKeys.ENROLMENT]: {},
  [reducerKeys.PROFILE]: {},

  [reducerKeys.CLASS_DETAIL]: {},
  [reducerKeys.PRIVILEGED_CONTENT]: {},
  [reducerKeys.TEACHER_NOTE]: {},

  classDetailLoading: false,
  privilegedContentLoading: false,

  isAnonymized: window.location.hash === '#anon',
  anonymousBackup: null,
});

export default (state = defaultState, { type, payload }) => {
  switch (type) {
    case actionTypes.LOAD_CLASS_DETAIL_FETCH:
      return state.set('classDetailLoading', true);

    case actionTypes.LOAD_CLASS_DETAIL_SUCCESS:
      return state.mergeDeep(payload.entities).set('classDetailLoading', false);

    case actionTypes.LOAD_CLASS_DETAIL_FAILURE:
      return state.set('classDetailLoading', false);

    case actionTypes.LOAD_PRIVILEGED_CONTENT_FETCH:
      return state.set('privilegedContentLoading', true);

    case actionTypes.LOAD_PRIVILEGED_CONTENT_SUCCESS: {
      const normalizedUpdater = createNormalizedStoreUpdater(
        [reducerKeys.PRIVILEGED_CONTENT, reducerKeys.TEACHER_NOTE],
        payload.entities
      );

      return normalizedUpdater(state).set('privilegedContentLoading', false);
    }

    case actionTypes.LOAD_PRIVILEGED_CONTENT_FAILURE:
      return state.set('privilegedContentLoading', false);

    // clear errors for save and delete
    case actionTypes.SAVE_TEACHER_NOTE_FETCH:
    case actionTypes.DELETE_TEACHER_NOTE_FETCH:
      return state.set('teacherNoteErrors', false);

    // display errors for save and fetch failure
    case actionTypes.SAVE_TEACHER_NOTE_FAILURE:
    case actionTypes.DELETE_TEACHER_NOTE_FAILURE:
      return state.set('teacherNoteErrors', payload.errors);

    case actionTypes.SAVE_TEACHER_NOTE_SUCCESS: {
      // set the note in the normalised store (insert or update)
      let newState = state.setIn(
        [reducerKeys.TEACHER_NOTE, payload.note.id.toString()],
        Immutable.fromJS(payload.note)
      );

      // if it's a new note, we need to add it's id to the privileged content list of teacher notes
      if (payload.isNew) {
        newState = newState.updateIn(
          [
            reducerKeys.PRIVILEGED_CONTENT,
            payload.classId,
            'teacher_notes_for_students',
          ],
          (teacherNotes) => teacherNotes.unshift(payload.note.id)
        );
      }

      return newState;
    }

    case actionTypes.DELETE_TEACHER_NOTE_SUCCESS: {
      // remove the note from normalised store
      let newState = state.update(reducerKeys.TEACHER_NOTE, (allNotes) =>
        allNotes.delete(payload.noteId.toString())
      );

      // remove the note from all the schoolclasses it's attached to. (if loaded)
      payload.schoolclasses.forEach((classId) => {
        const notesPath = [
          reducerKeys.PRIVILEGED_CONTENT,
          classId.toString(),
          'teacher_notes_for_students',
        ];
        const classNotes = state.getIn(notesPath);
        const noteIndex =
          classNotes &&
          classNotes.findIndex((noteId) => noteId === payload.noteId);
        if (classNotes && typeof noteIndex !== 'undefined' && noteIndex > -1) {
          newState = newState.setIn(notesPath, classNotes.delete(noteIndex));
        }
      });
      return newState;
    }

    // run for both request and failure for optimistic updates, failure restores original value
    case actionTypes.LOCK_LESSON_RESOURCE_REQUEST:
    case actionTypes.LOCK_LESSON_RESOURCE_FAILURE: {
      const { classId, resourceId, showToStudents } = payload;
      const lessonResourceLockPath = [
        reducerKeys.PRIVILEGED_CONTENT,
        classId.toString(),
        'lesson_resource_switches',
      ];
      const lessonResourceLocks = state.getIn(lessonResourceLockPath);
      // Clever way of updating an item in a list
      const newLessonResourceLocks = lessonResourceLocks.map((lock) =>
        lock.get('resource_id') === resourceId
          ? lock.set('show_to_students', showToStudents)
          : lock
      );
      return state.setIn(lessonResourceLockPath, newLessonResourceLocks);
    }

    default:
      return state;
  }
};

/* Action Creators */
const loadClassDetail = {
  signal: (classId) => ({
    type: actionTypes.LOAD_CLASS_DETAIL_SIGNAL,
    payload: { classId },
  }),
  request: (classId) => ({
    type: actionTypes.LOAD_CLASS_DETAIL_FETCH,
    payload: { classId },
  }),
  success: (classId, entities) => ({
    type: actionTypes.LOAD_CLASS_DETAIL_SUCCESS,
    payload: {
      classId,
      entities,
    },
  }),
  failure: (errors) => ({
    type: actionTypes.LOAD_CLASS_DETAIL_FAILURE,
    payload: { errors },
  }),
};

const loadPrivilegedContent = {
  signal: (classId) => ({
    type: actionTypes.LOAD_PRIVILEGED_CONTENT_SIGNAL,
    payload: { classId },
  }),
  request: (classId) => ({
    type: actionTypes.LOAD_PRIVILEGED_CONTENT_FETCH,
    payload: { classId },
  }),
  success: (classId, entities) => ({
    type: actionTypes.LOAD_PRIVILEGED_CONTENT_SUCCESS,
    payload: {
      classId,
      entities,
    },
  }),
  failure: (errors) => ({
    type: actionTypes.LOAD_PRIVILEGED_CONTENT_FAILURE,
    payload: { errors },
  }),
};

const teacherNotes = {
  save: {
    signal: (lessonId, classId, note, attachment, removeAttachment) => ({
      type: actionTypes.SAVE_TEACHER_NOTE_SIGNAL,
      payload: { lessonId, classId, note, attachment, removeAttachment },
    }),
    request: (lessonId, classId, note) => ({
      type: actionTypes.SAVE_TEACHER_NOTE_FETCH,
      payload: { lessonId, classId, note },
    }),
    success: (lessonId, classId, note, isNew, hasAttachment) => ({
      type: actionTypes.SAVE_TEACHER_NOTE_SUCCESS,
      payload: {
        lessonId,
        classId,
        note,
        isNew,
        hasAttachment,
      },
      ...trackAnalytics('saved a note to students', {
        lessonId,
        noteId: note.id,
        shareWithAll: note.schoolclasses.length > 1,
        hasLink: !!note.link_url,
        hasAttachment,
      }),
    }),
    failure: (errors) => ({
      type: actionTypes.SAVE_TEACHER_NOTE_FAILURE,
      payload: { errors },
    }),
  },
  delete: {
    signal: (lessonId, noteId, schoolclasses) => ({
      type: actionTypes.DELETE_TEACHER_NOTE_SIGNAL,
      payload: { lessonId, noteId, schoolclasses },
    }),
    request: (lessonId, noteId, schoolclasses) => ({
      type: actionTypes.DELETE_TEACHER_NOTE_FETCH,
      payload: { lessonId, noteId, schoolclasses },
    }),
    success: (lessonId, noteId, schoolclasses) => ({
      type: actionTypes.DELETE_TEACHER_NOTE_SUCCESS,
      payload: {
        lessonId,
        noteId,
        schoolclasses,
      },
      ...trackAnalytics('deleted a note to students', {
        lessonId,
        noteId,
      }),
    }),
    failure: (errors) => ({
      type: actionTypes.DELETE_TEACHER_NOTE_FAILURE,
      payload: { errors },
    }),
  },
};

const lockLessonResource = {
  signal: (classId, resourceId, showToStudents) => ({
    type: actionTypes.LOCK_LESSON_RESOURCE_SIGNAL,
    payload: { classId, resourceId, showToStudents },
  }),
  request: (classId, resourceId, showToStudents) => ({
    type: actionTypes.LOCK_LESSON_RESOURCE_REQUEST,
    payload: { classId, resourceId, showToStudents },
  }),
  success: (classId, resourceId, showToStudents) => ({
    type: actionTypes.LOCK_LESSON_RESOURCE_SUCCESS,
    payload: {
      classId,
      resourceId,
      showToStudents,
    },
    ...trackAnalytics('toggled lesson resource visibility', {
      resourceId,
      showToStudents,
      origin: ORIGIN.COURSE_PAGE,
    }),
  }),
  failure: (classId, resourceId, showToStudents, errors) => ({
    type: actionTypes.LOCK_LESSON_RESOURCE_FAILURE,
    payload: { errors },
  }),
};

// Analytics only actions
const accessLessonResource = (resourceId) => ({
  type: actionTypes.ANALYTICS_ACCESS_LESSON_RESOURCE,
  ...trackAnalytics('clicked lesson resource', {
    resourceId,
    origin: ORIGIN.COURSE_PAGE,
  }),
});

export const actions = {
  teacherNotes,
  loadClassDetail,
  loadPrivilegedContent,
  lockLessonResource,
  accessLessonResource,
};

export const selectors = {
  getAllClassEnrolments: (state) => state.class.get(reducerKeys.ENROLMENT),
  getAllProfiles: (state) => state.class.get(reducerKeys.PROFILE),
  getAllClassDetail: (state) => state.class.get(reducerKeys.CLASS_DETAIL),
  getClassDetailLoading: (state) => state.class.get('classDetailLoading'),
  getAllPrivilegedContent: (state) =>
    state.class.get(reducerKeys.PRIVILEGED_CONTENT),
  getPrivilegedContentLoading: (state) =>
    state.class.get('privilegedContentLoading'),
  getAllTeacherNotes: (state) => state.class.get(reducerKeys.TEACHER_NOTE),
  getTeacherNoteErrors: (state) => state.class.get('teacherNoteErrors'),
};
